import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, ArrowUp, ArrowDown, Save, X, Settings2, ChevronLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";

const APPROVAL_TYPES = [
  "expense", "purchase", "contract", "document", "hiring", "vendor",
  "project", "discount", "pricing", "site_publish", "operational_exception", "other",
];

const ROLES = [
  { v: "division_admin",  label: "Division Admin" },
  { v: "company_admin",   label: "Company Admin" },
  { v: "functional_admin",label: "Functional Admin" },
  { v: "enterprise_admin",label: "Enterprise Admin" },
  { v: "owner_ceo",       label: "Owner / CEO" },
];

const emptyStep = () => ({ role: "enterprise_admin", label: "", threshold_amount: null });

const ChainEditor = ({ chain, setChain }) => {
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= chain.length) return;
    const copy = [...chain];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setChain(copy);
  };
  const remove = (i) => setChain(chain.filter((_, idx) => idx !== i));
  const update = (i, patch) => setChain(chain.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const add = () => setChain([...chain, emptyStep()]);

  return (
    <div className="space-y-2">
      {chain.length === 0 && (
        <p className="text-xs text-textmuted italic px-3 py-4 border border-dashed border-seam rounded-md">
          No chain yet. Add a step to require multi-level approval. Without a chain,
          the policy falls back to the single required role above.
        </p>
      )}
      {chain.map((step, i) => (
        <div
          key={i}
          data-testid={`chain-step-${i}`}
          className="flex flex-wrap items-center gap-2 rounded-md border border-seam bg-elevated/40 p-3"
        >
          <span className="text-[10px] font-mono text-champagne w-6">#{i + 1}</span>
          <select
            data-testid={`chain-step-role-${i}`}
            value={step.role}
            onChange={(e) => update(i, { role: e.target.value })}
            className="rounded bg-obsidian border border-seam text-xs text-textmain px-2 py-1"
          >
            {ROLES.map((r) => (<option key={r.v} value={r.v}>{r.label}</option>))}
          </select>
          <input
            type="text"
            placeholder="Step label (optional)"
            data-testid={`chain-step-label-${i}`}
            value={step.label}
            onChange={(e) => update(i, { label: e.target.value })}
            className="flex-1 min-w-[160px] rounded bg-obsidian border border-seam text-xs text-textmain px-2 py-1"
          />
          <input
            type="number"
            placeholder="Only above $"
            data-testid={`chain-step-threshold-${i}`}
            value={step.threshold_amount ?? ""}
            onChange={(e) => update(i, {
              threshold_amount: e.target.value === "" ? null : Number(e.target.value),
            })}
            className="w-32 rounded bg-obsidian border border-seam text-xs text-textmain px-2 py-1"
          />
          <div className="flex items-center gap-1 ml-auto">
            <button
              type="button"
              onClick={() => move(i, -1)}
              disabled={i === 0}
              data-testid={`chain-step-up-${i}`}
              className="p-1.5 rounded border border-seam text-textmuted hover:text-champagne disabled:opacity-30"
            ><ArrowUp className="w-3 h-3" /></button>
            <button
              type="button"
              onClick={() => move(i, 1)}
              disabled={i === chain.length - 1}
              data-testid={`chain-step-down-${i}`}
              className="p-1.5 rounded border border-seam text-textmuted hover:text-champagne disabled:opacity-30"
            ><ArrowDown className="w-3 h-3" /></button>
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`chain-step-remove-${i}`}
              className="p-1.5 rounded border border-seam text-textmuted hover:text-[#E5A09E]"
            ><Trash2 className="w-3 h-3" /></button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid="chain-add-step"
        className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-champagne/40 text-champagne hover:bg-champagne/10"
      >
        <Plus className="w-3.5 h-3.5" /> Add step
      </button>
    </div>
  );
};

const PolicyForm = ({ initial, entities, onSaved, onCancel }) => {
  const isEdit = !!initial?.id;
  const [entityId, setEntityId] = useState(initial?.entity_id ?? "");
  const [type, setType] = useState(initial?.type ?? "expense");
  const [thresholdAmount, setThresholdAmount] = useState(initial?.threshold_amount ?? "");
  const [requiredRole, setRequiredRole] = useState(initial?.required_role ?? "enterprise_admin");
  const [escalateRole, setEscalateRole] = useState(initial?.escalate_role ?? "owner_ceo");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [chain, setChain] = useState(initial?.chain ?? []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/approvals/policies", {
        entity_id: entityId || null,
        type,
        threshold_amount: thresholdAmount === "" ? null : Number(thresholdAmount),
        required_role: requiredRole,
        escalate_role: escalateRole,
        description,
        chain: chain.map((s) => ({
          role: s.role,
          label: s.label || "",
          threshold_amount: s.threshold_amount === "" ? null : s.threshold_amount,
        })),
      });
      toast.success(isEdit ? "Policy updated" : "Policy created");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="regal-card p-5 space-y-4" data-testid="policy-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-eyebrow block mb-1 text-[10px]">Business scope</label>
          <select
            data-testid="policy-entity"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            disabled={isEdit}
            className="w-full rounded bg-obsidian border border-seam text-sm text-textmain px-2 py-1.5"
          >
            <option value="">Enterprise-wide (all businesses)</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1 text-[10px]">Approval type</label>
          <select
            data-testid="policy-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            disabled={isEdit}
            className="w-full rounded bg-obsidian border border-seam text-sm text-textmain px-2 py-1.5"
          >
            {APPROVAL_TYPES.map((t) => (
              <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1 text-[10px]">Default required role</label>
          <select
            data-testid="policy-required-role"
            value={requiredRole}
            onChange={(e) => setRequiredRole(e.target.value)}
            className="w-full rounded bg-obsidian border border-seam text-sm text-textmain px-2 py-1.5"
          >
            {ROLES.map((r) => (<option key={r.v} value={r.v}>{r.label}</option>))}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1 text-[10px]">Escalate to</label>
          <select
            data-testid="policy-escalate-role"
            value={escalateRole}
            onChange={(e) => setEscalateRole(e.target.value)}
            className="w-full rounded bg-obsidian border border-seam text-sm text-textmain px-2 py-1.5"
          >
            {ROLES.map((r) => (<option key={r.v} value={r.v}>{r.label}</option>))}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1 text-[10px]">Auto-eligible below $ (optional)</label>
          <input
            type="number"
            data-testid="policy-threshold"
            value={thresholdAmount}
            onChange={(e) => setThresholdAmount(e.target.value)}
            className="w-full rounded bg-obsidian border border-seam text-sm text-textmain px-2 py-1.5"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-1 text-[10px]">Description</label>
          <input
            type="text"
            data-testid="policy-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded bg-obsidian border border-seam text-sm text-textmain px-2 py-1.5"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="label-eyebrow text-[10px]">Multi-step chain (top to bottom)</span>
          <span className="text-[10px] text-textmuted">
            Steps run in order. "Only above $" skips the step when the request amount is below.
          </span>
        </div>
        <ChainEditor chain={chain} setChain={setChain} />
      </div>

      <div className="flex items-center gap-2 justify-end pt-2 border-t border-seam">
        <button
          type="button"
          onClick={onCancel}
          data-testid="policy-cancel"
          className="text-xs px-3 py-2 rounded-md border border-seam text-textmuted hover:text-textmain"
        >
          <X className="inline w-3.5 h-3.5 mr-1" /> Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="policy-save"
          className="text-xs px-3 py-2 rounded-md bg-champagne text-obsidian hover:bg-champagne-soft"
        >
          {saving ? <Loader2 className="inline w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="inline w-3.5 h-3.5 mr-1" />}
          {isEdit ? "Save policy" : "Create policy"}
        </button>
      </div>
    </div>
  );
};

const ApprovalPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} for new | policy for edit

  const entityById = useMemo(() => {
    const m = {};
    entities.forEach((e) => { m[e.id] = e.name; });
    return m;
  }, [entities]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, e] = await Promise.all([
        api.get("/approvals/policies"),
        api.get("/enterprise/entities"),
      ]);
      setPolicies(p.data || []);
      setEntities(e.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load policies");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this policy? Existing approvals will keep their snapshot.")) return;
    try {
      await api.delete(`/approvals/policies/${id}`);
      toast.success("Policy deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Layout>
      <section className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow">Governance</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">
            Approval Chain Builder
          </h1>
          <p className="text-textmuted mt-3 max-w-2xl leading-relaxed">
            Design the exact path a request must walk before it becomes real.
            Any policy can have a single required role or an ordered chain
            of reviewers, with per-step dollar thresholds.
          </p>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Link
            to="/approvals"
            data-testid="back-to-approvals"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-seam text-textmuted hover:text-champagne hover:border-champagne/40"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Approval Center
          </Link>
          <button
            type="button"
            onClick={() => setEditing({})}
            data-testid="new-policy-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-champagne text-obsidian hover:bg-champagne-soft"
          >
            <Plus className="w-3.5 h-3.5" /> New policy
          </button>
        </div>
      </section>

      {editing && (
        <div className="mb-6">
          <PolicyForm
            initial={editing}
            entities={entities}
            onSaved={() => { setEditing(null); load(); }}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-champagne animate-spin" />
        </div>
      ) : policies.length === 0 ? (
        <div className="regal-card p-12 text-center">
          <Settings2 className="w-8 h-8 text-champagne mx-auto mb-3" strokeWidth={1.4} />
          <p className="text-textmuted">No policies yet — everything defaults to Enterprise Admin.</p>
        </div>
      ) : (
        <div className="regal-card divide-y divide-seam" data-testid="policies-list">
          {policies.map((p) => (
            <div key={p.id} className="px-5 py-4" data-testid={`policy-row-${p.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-champagne">
                      {p.type.replace(/_/g, " ")}
                    </span>
                    <span className="text-[11px] text-textmuted">
                      {p.entity_id ? entityById[p.entity_id] || p.entity_id : "Enterprise-wide"}
                    </span>
                    {p.threshold_amount != null && (
                      <span className="text-[10px] font-mono text-textmuted">
                        auto-eligible ≤ ${p.threshold_amount.toLocaleString()}
                      </span>
                    )}
                  </div>
                  {p.description && (
                    <p className="text-sm text-textmain mt-1">{p.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {(p.chain?.length || 0) === 0 ? (
                      <span className="text-[11px] text-textmuted">
                        Single-step → <span className="text-champagne">{p.required_role.replace(/_/g, " ")}</span>
                      </span>
                    ) : (
                      p.chain.map((s, i) => (
                        <React.Fragment key={i}>
                          <span className="text-[10px] px-2 py-0.5 rounded-full border border-champagne/30 bg-champagne/5 text-champagne">
                            {s.label || s.role.replace(/_/g, " ")}
                            {s.threshold_amount != null && ` · >${s.threshold_amount}`}
                          </span>
                          {i < p.chain.length - 1 && (
                            <span className="text-textmuted text-xs">→</span>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(p)}
                    data-testid={`edit-policy-${p.id}`}
                    className="text-xs px-3 py-1.5 rounded-md border border-seam text-textmuted hover:text-champagne"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(p.id)}
                    data-testid={`delete-policy-${p.id}`}
                    className="text-xs px-3 py-1.5 rounded-md border border-seam text-textmuted hover:text-[#E5A09E]"
                  >
                    <Trash2 className="inline w-3 h-3 mr-1" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
};

export default ApprovalPolicies;
